import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import Switch from '../../../../molecules/Switch'
import SydSwitch from '../../../../commonDesign/SydSwitch'
import { IconPickerField } from '../../../../commonDesign/IconPicker'
import InputColorPicker from '../../../../molecules/InputColorPicker'
import presets from './presets'

// Wealthbox `/v1/tasks` `task_type` enum.
const taskTypeOptions = [
  { label: 'All (default)', value: '' },
  { label: 'Parents only', value: 'parents' },
  { label: 'Subtasks only', value: 'subtasks' }
]

// We intentionally do NOT expose Wealthbox's `resource_type` as a standalone
// admin filter. Wealthbox treats `resource_id` + `resource_type` as a paired
// filter ("tasks linked to THIS resource of THIS type") - the documented URL
// template lists them together and the events analogue (`wbox events list`)
// requires both at once. Sending `resource_type` alone is silently ignored.
// In Client scope, `resource_id` + `resource_type=Contact` is set server-side
// in `buildTaskParams`. In My Book scope, neither is set because we want all
// tasks assigned to the user regardless of what they're linked to.

const presetOptions = [
  { label: 'Select a preset...', value: '' },
  ...Object.entries(presets).map(([key, preset]) => ({
    label: preset.displayName,
    value: key
  }))
]

const scopeOptions = [
  { label: 'Client — tasks linked to the current client', value: 'client' },
  { label: 'My Book — all tasks assigned to me', value: 'user' },
  { label: 'All — every task in the workspace (no automatic binding)', value: 'all' }
]

const createdByScopeOptions = [
  { label: 'Anyone (default)', value: '' },
  { label: 'Tasks I created', value: 'me' }
]

const styleVariantOptions = [
  { label: 'Default (Medium)', value: 'default' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' }
]

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

function WealthboxMetricConfigEditor ({ fieldPrefix }) {
  const { control, watch } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`

  const presetValue = watch(`${configPrefix}.preset`)
  const hideTitleValue = watch(`${configPrefix}.hideTitle`)
  const hideLabelValue = watch(`${configPrefix}.hideLabel`)
  const hideIconValue = watch(`${configPrefix}.hideIcon`)

  const selectedPreset = useMemo(() => {
    return presetValue ? presets[presetValue] : null
  }, [presetValue])

  // Each preset declares which config fields it consumes via `usesFields`.
  // Driving visibility off that declaration keeps the form honest: if a field
  // doesn't feed any param on the current preset's API call, it's hidden.
  const presetUsesField = (field) => selectedPreset?.usesFields?.includes(field) ?? false
  const showUpdatedWithin = presetUsesField('updatedWithinDays')
  const showStaleAfter = presetUsesField('staleAfterDays')

  return (
    <Grid container spacing={2}>
      {/* Core */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Core</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.preset`}
          control={control}
          render={({ field }) => (
            <SydLabel required label='Preset' description='Select the metric type this component displays'>
              <SydSelect
                options={presetOptions}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.scope`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Scope' description='Filter tasks to a specific client or across your full book'>
              <SydSelect
                options={scopeOptions}
                value={field.value || 'client'}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      {/* Appearance */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Appearance</div>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideTitle`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show title' description='Toggle off to hide the title entirely'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideTitleValue && (
            <Grid item xs={10}>
              <Controller
                name={`${configPrefix}.title`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Title' description='Override the preset default title'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <SydInput
                        {...field}
                        value={field.value || ''}
                        placeholder={selectedPreset?.defaults?.title || 'Title'}
                        minWidth='0'
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideIcon`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show title icon' description='Toggle off to hide the icon entirely'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideIconValue && (
            <Grid item xs={5}>
              <Controller
                name={`${configPrefix}.icon`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Icon' description='Icon name (e.g. wealthboxSmall)'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <IconPickerField
                        value={field.value || selectedPreset?.defaults?.icon || null}
                        onChange={field.onChange}
                        placeholder={selectedPreset?.defaults?.icon || 'Select an icon...'}
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
          {!hideIconValue && (
            <Grid item xs={5}>
              <Controller
                name={`${configPrefix}.iconColor`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Icon color' description='Hex (#fff, #ffffff, #ffffffaa) or rgba(r,g,b,a). Defaults to primary color when blank.'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <InputColorPicker
                        value={field.value || ''}
                        onChange={(v) => field.onChange(v || null)}
                        placeholder='#3D7DDD or rgba(61,125,221,1)'
                        style={{ width: '100%' }}
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideLabel`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show label' description='Toggle off to hide the label entirely'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideLabelValue && (
            <Grid item xs={10}>
              <Controller
                name={`${configPrefix}.label`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Label' description='Subtitle shown below the value'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <SydInput
                        {...field}
                        value={field.value || ''}
                        placeholder={selectedPreset?.defaults?.label || 'Label'}
                        minWidth='0'
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Controller
              name={`${configPrefix}.format`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Value Format' description='numeral.js format (e.g. 0,0)'>
                  <div style={{ width: '100%', minWidth: 0 }}>
                    <SydInput
                      {...field}
                      value={field.value || ''}
                      placeholder={selectedPreset?.defaults?.format || '0,0'}
                      minWidth='0'
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name={`${configPrefix}.valueColor`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Value color' description='Hex (#fff, #ffffff, #ffffffaa) or rgba(r,g,b,a). Inherits when blank.'>
                  <div style={{ width: '100%', minWidth: 0 }}>
                    <InputColorPicker
                      value={field.value || ''}
                      onChange={(v) => field.onChange(v || null)}
                      placeholder='#3D7DDD or rgba(61,125,221,1)'
                      style={{ width: '100%' }}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Controller
              name={`${fieldPrefix}.styleVariant`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Style Variant' description='Visual presentation style'>
                  <SydSelect
                    options={styleVariantOptions}
                    value={field.value || 'default'}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </SydLabel>
              )}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Capabilities + Filtering side-by-side */}
      <Grid item xs={12}>
        <div style={{ display: 'flex', gap: '32px' }}>
          <div style={{ width: '220px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={sectionHeading}>Capabilities</div>
            <Controller
              name={`${configPrefix}.enableEdit`}
              control={control}
              render={({ field }) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                  <span style={{ fontSize: 'var(--font-size-sm)' }}>Allow editing</span>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </div>
              )}
            />
            <Controller
              name={`${configPrefix}.formMode`}
              control={control}
              render={({ field }) => (
                <SydLabel
                  label='Form Mode'
                  description='Simple: name + due date only. Calendar: also exposes priority, category, and description.'
                >
                  <SydSelect
                    minWidth={0}
                    options={[
                      { label: 'Simple (date only)', value: 'simple' },
                      { label: 'Calendar (full edit form)', value: 'calendar' }
                    ]}
                    value={field.value || 'simple'}
                    onChange={(e) => field.onChange(e.target.value || null)}
                  />
                </SydLabel>
              )}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={sectionHeading}>Filtering</div>
            <Grid container spacing={2} style={{ marginTop: '4px' }}>
              {showUpdatedWithin && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.updatedWithinDays`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Updated Within (days)'>
                        <SydInput
                          type='number'
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value
                            if (!raw || !raw.trim()) { field.onChange(null); return }
                            const parsed = parseInt(raw, 10)
                            field.onChange(isNaN(parsed) ? null : parsed)
                          }}
                          placeholder='7'
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              {showStaleAfter && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.staleAfterDays`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Stale After (days)'>
                        <SydInput
                          type='number'
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value
                            if (!raw || !raw.trim()) { field.onChange(null); return }
                            const parsed = parseInt(raw, 10)
                            field.onChange(isNaN(parsed) ? null : parsed)
                          }}
                          placeholder='30'
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                <Controller
                  name={`${configPrefix}.taskType`}
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Task Type'>
                      <SydSelect
                        options={taskTypeOptions}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name={`${configPrefix}.createdByScope`}
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Created By'>
                      <SydSelect
                        options={createdByScopeOptions}
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name={`${configPrefix}.teamId`}
                  control={control}
                  render={({ field }) => (
                    <SydLabel
                      label='Team ID (optional)'
                      description='Wealthbox team id from your workspace; blank = no team filter'
                    >
                      <SydInput
                        type='number'
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value
                          if (!raw || !raw.trim()) { field.onChange(null); return }
                          const parsed = parseInt(raw, 10)
                          field.onChange(isNaN(parsed) ? null : parsed)
                        }}
                        placeholder='e.g. 12'
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name={`${configPrefix}.includeCompleted`}
                  control={control}
                  render={({ field }) => (
                    <label className='flex items-center gap-2' style={{ minHeight: '54px', cursor: 'pointer' }}>
                      <SydSwitch
                        label='Include completed'
                        value={!!field.value}
                        onChange={(_event, newValue) => field.onChange(newValue)}
                      />
                      <span className='font-size-sm'>Include completed</span>
                    </label>
                  )}
                />
              </Grid>
            </Grid>
          </div>
        </div>
      </Grid>
    </Grid>
  )
}

WealthboxMetricConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default WealthboxMetricConfigEditor
